import React, { useCallback, useEffect, useState } from 'react';
import Button from 'components/common/button';
import TextField from '@mui/material/TextField';
import { StateType } from 'reducer';
import { NonPodiatryOptionsType } from 'types/types';
import {
  WhatTreatmentsItemWithText,
  usePersistNonPodiatryReducer,
} from 'reducer/nonPodiatry';
import clsx from 'clsx';
import MetaTag from 'components/meta';
import styles from 'components/nonPodiatry/page.module.scss';
import Label from 'components/common/label';

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  options: NonPodiatryOptionsType[] | NonPodiatryOptionsType;
  multiple?: boolean;
  isFilter?: boolean;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function WhatTreatmentsRecommendedProvider({
  nextPage,
  nextOrSubmitButton,
  options,
  isFilter,
  multiple = true,
  actionPage,
}: Props) {
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState<string | null>('');
  const [inputText, setInputText] = useState<WhatTreatmentsItemWithText[]>([]);
  const [inputTextError, setInputTextError] = useState<
    WhatTreatmentsItemWithText[]
  >([]);

  const [questionList, setQuestionList] = useState<string[]>([]);
  const [error, setError] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [state, dispatch] = usePersistNonPodiatryReducer();

  useEffect(() => {
    if (isFilter && Array.isArray(options)) {
      const findOptions = options.find(
        (obj: NonPodiatryOptionsType) => obj.name === actionPage,
      );
      if (findOptions) {
        try {
          setQuestionList(findOptions.options);
          setTitle(findOptions.title);
          setSubTitle(findOptions.subTitle);
        } catch (error) {
          console.error('🚀 ~ Error on filter options', error);
        }
      }
    } else if (!Array.isArray(options)) {
      setQuestionList(options.options);
      setTitle(options.title);
      setSubTitle(options.subTitle);
    }
    if (state) {
      const data = state.find(
        (object: StateType) => object.name === actionPage,
      );
      if (data) {
        setSelectedItems(data.value);
        setInputText(data.options);
      }
    }
  }, [actionPage, isFilter, options, state]);

  const onSelectOptions = useCallback((item: string) => {
    if (multiple) {
      setSelectedItems((old) => {
        if (old.find((value) => value === item)) {
          return old.filter((data) => data !== item);
        } return [...old, item];
      });
    } else {
      setSelectedItems([item]);
    }

    if (['X-Ray', 'MRI', 'CAT Scan'].includes(item)) {
      setInputText((old) => {
        if (old.find((value) => value.name === item)) {
          return old.filter((data) => data.name !== item);
        } return [...old, { name: item, text: '' }];
      });
    }
    setError(false);
  }, []);

  const clickNextOrSubmit = async () => {
    for (let i = 0; i < inputText.length; i += 1) {
      const item = inputText[i];
      if (item.text === '') {
        setInputTextError(inputText);
        return;
      }
    }

    await dispatch({
      type: actionPage,
      name: title,
      value: selectedItems,
      options: inputText,
    });
    nextOrSubmitButton(nextPage);
  };

  const onChangeText = (
    name: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInputText((old) => {
      if (old.find((value) => value.name === name)) {
        const newItems = old.filter((data) => data.name !== name);
        return [...newItems, { name, text: event.target.value }];
      } return [...old, { name, text: event.target.value }];
    });
    setInputTextError((old) => {
      if (old.find((value) => value.name === name)) {
        return old.filter((data) => data.name !== name);
      } return old;
    });
  };

  return (
    <>
      <MetaTag title={title} />
      <h2 className={`marginTopZero ${styles.titleText}`}>{title}</h2>
      {subTitle && <p className={styles.titleTextSubText}>{subTitle}</p>}
      {error && (
      <p className="error-message">
        {' '}
        Please select at least one option.
      </p>
      )}
      <div>
        {questionList.map((option, index) => {
          const isSelected = selectedItems.includes(option);
          const inputBoxError = inputTextError.find(
            (item) => item.name === option,
          );
          const inputBoxItem = inputText.find(
            (item) => item.name === option,
          );
          return (
            <div key={option}>
              <p
                className={clsx(
                  'option',
                  { 'selected-option': isSelected },
                )}
                onClick={() => onSelectOptions(option)}
                onKeyUp={() => {}}
              >
                {option}
              </p>
              {isSelected
                    && ['X-Ray', 'MRI', 'CAT Scan'].includes(option) && (
                      <>
                        <Label>
                          Where was this taken?
                        </Label>
                        <TextField
                          value={inputBoxItem?.text}
                          placeholder="Type..."
                          className="w-full"
                          sx={{
                            '& .MuiInputBase-root': { borderRadius: '4px' },
                            marginTop: 0.5,
                            marginBottom: 1,
                            width: '100%',
                          }}
                          size="small"
                          error={inputBoxError?.text === ''}
                          onChange={(event) => onChangeText(option, event)}
                        />
                      </>
              )}
            </div>
          );
        })}
      </div>
      <div className="next-button-fixed">
        <Button onClick={clickNextOrSubmit}>
          Next
        </Button>
      </div>
    </>
  );
}

export default WhatTreatmentsRecommendedProvider;
